function getBaseURL(cfg = {}) {
  const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
  // CRITICAL: Use .trim() to remove trailing newlines from Vercel env vars
  const url = (
    cfg.baseURL ||
    env.VITE_API_BASE_URL ||
    env.VITE_API_URL ||
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) ||
    (typeof window !== 'undefined' ? window.API_BASE : null) ||
    'https://apps.quantrolix.ai/api/quantrolix'
  );
  return typeof url === 'string' ? url.trim() : url;
}

function withTimeout(ms, controller) {
  return setTimeout(() => controller.abort(), ms);
}

async function jsonRequest(baseURL, path, opts = {}) {
  const url = path.startsWith('http') ? path : `${baseURL}${path}`;
  const controller = new AbortController();
  const timer = withTimeout(opts.timeout ?? 10000, controller);
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('auth_token')) || null;

  try {
    const res = await fetch(url, {
      method: opts.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(opts.headers || {}),
      },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      signal: controller.signal,
      credentials: 'include',
      mode: 'cors',
    });
    clearTimeout(timer);

    // Handle rate limiting with specific error message
    if (res.status === 429) {
      const rateLimitData = await res.json().catch(() => ({}));
      const err = new Error(`Rate limit exceeded. ${rateLimitData.detail || 'Please try again later.'}`);
      err.status = 429;
      err.retryAfter = rateLimitData.retry_after;
      err.tier = rateLimitData.tier;
      err.upgradeUrl = rateLimitData.upgrade_url;
      throw err;
    }

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const err = new Error(`HTTP ${res.status}: ${res.statusText} ${text}`);
      err.status = res.status;
      throw err;
    }

    return res.json();
  } catch (error) {
    clearTimeout(timer);

    // Enhance error with context
    if (error.name === 'AbortError') {
      error.message = `Request timeout after ${opts.timeout ?? 10000}ms`;
    }

    throw error;
  }
}

export function createClient(config = {}) {
  const baseURL = getBaseURL(config);
  return {
    baseURL,
    health: () => jsonRequest(baseURL, '/api/health'),
    metrics: () => jsonRequest(baseURL, '/api/dashboard/metrics'),
    alerts: { recent: () => jsonRequest(baseURL, '/api/alerts/recent') },
    agents: { status: () => jsonRequest(baseURL, '/api/agents/status') },
    scrapers: {
      list: () => jsonRequest(baseURL, '/api/scrapers'),
      start: (id) => jsonRequest(baseURL, `/api/scrapers/${id}/start`, { method: 'POST' }),
      stop: (id) => jsonRequest(baseURL, `/api/scrapers/${id}/stop`, { method: 'POST' }),
    },
    sellerAccounts: {
      list: () => jsonRequest(baseURL, '/api/seller-accounts'),
      create: (payload) => jsonRequest(baseURL, '/api/seller-accounts', { method: 'POST', body: payload }),
      update: (id, payload) => jsonRequest(baseURL, `/api/seller-accounts/${id}`, { method: 'PUT', body: payload }),
      remove: (id) => jsonRequest(baseURL, `/api/seller-accounts/${id}`, { method: 'DELETE' }),
      test: (id) => jsonRequest(baseURL, `/api/seller-accounts/${id}/test`, { method: 'POST' }),
      sync: (id) => jsonRequest(baseURL, `/api/seller-accounts/${id}/sync`, { method: 'POST' }),
    },
    products: {
      search: (query, options = {}) => {
        const params = new URLSearchParams();
        if (query) params.set('query', query);
        if (options.limit != null) params.set('limit', String(options.limit));
        if (options.filters) {
          const { minPrice, maxPrice, category, marketplace } = options.filters;
          if (minPrice != null) params.set('min_price', String(minPrice));
          if (maxPrice != null) params.set('max_price', String(maxPrice));
          if (marketplace && marketplace !== 'all') params.set('marketplace', marketplace);
        }
        const qs = params.toString() ? `?${params}` : '';
        // Backend serves /api/products/search (public, no auth). The older
        // /api/products/search-public path was never deployed → 404.
        return jsonRequest(baseURL, `/api/products/search${qs}`);
      },
      barcodeScan: (payload) => {
        let body = {};
        if (typeof payload === 'string') body = { barcode: payload };
        else if (payload && typeof payload === 'object') {
          const { barcode, upc, ean, ...rest } = payload;
          body = { barcode: barcode || upc || ean, ...rest };
        }
        return jsonRequest(baseURL, '/api/scans/barcode', { method: 'POST', body });
      },
    },
    marketplace: {
      search: (asin, options = {}) => {
        if (!asin) throw new Error('ASIN is required for marketplace search');
        // Use the existing product search endpoint with ASIN as query
        const params = new URLSearchParams();
        params.set('query', asin);
        if (options.limit != null) params.set('limit', String(options.limit));
        const qs = params.toString() ? `?${params}` : '';
        return jsonRequest(baseURL, `/api/products/search${qs}`, {
          timeout: 15000 // Standard timeout for existing endpoint
        });
      }
    },
    scans: {
      create: (asin, options = {}) => {
        // Use product search as a fallback since /api/scans doesn't exist
        const params = new URLSearchParams();
        params.set('query', asin);
        if (options.limit != null) params.set('limit', String(options.limit));
        const qs = params.toString() ? `?${params}` : '';
        return jsonRequest(baseURL, `/api/products/search${qs}`, {
          timeout: 15000
        });
      },
      status: (scanId) => {
        // Return a mock status since this endpoint doesn't exist
        return Promise.resolve({
          id: scanId,
          status: 'completed',
          message: 'Using fallback product search',
          timestamp: new Date().toISOString()
        });
      }
    },
  };
}

export default { createClient };
